<template lang="pug">
.notification-bell(ref="root")
  .bell-trigger(@click="togglePanel", ref="trigger")
    q-icon(name="notifications", size="28px")
    .bell-badge(v-if="unreadCount > 0") {{ unreadCount > 99 ? "99+" : unreadCount }}

  .notification-panel(v-if="panelOpen", ref="panel", :style="panelStyle")
    .notification-panel-arrow
    .notification-panel-header
      span Уведомления
      q-btn.notification-read-all(
        flat,
        dense,
        no-caps,
        size="sm",
        label="Прочитать все",
        :disable="unreadCount === 0 || markingAllRead",
        :loading="markingAllRead",
        @click="markAllRead"
      )
    .notification-panel-body(ref="listBody")
      template(v-if="notifications.length > 0 || loading")
        .notification-item(
          v-for="item in notifications",
          :key="item.id",
          :class="{ 'notification-item--unread': !item.read, 'notification-item--clickable': item.url, 'notification-item--dim': item.read && dimRead }",
          @click="handleItemClick(item)"
        )
          .notification-item-text {{ item.text }}
          .notification-item-date {{ formatDate(item.created_at) }}
        .notification-sentinel(v-observe-visibility="onSentinelVisible")
        .notification-loading(v-if="loading")
          q-spinner(color="primary", size="24px")
      .notification-empty(v-else) Нет уведомлений
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import backend from "@/api";
import { useRouter } from "@/routing/router";
import { useCable } from "@/plugins/actioncable-vue";
import { authToken, useReLogin } from "@/services/useAuth";

const CACHE_KEY = "notifications_cache";
const CACHE_LIMIT = 100;
const PANEL_WIDTH = 380;
const ROWS_PER_PAGE = 20;

const router = useRouter();
const cable = useCable();
const cableContext = { _uid: "notification-bell" };

const root = ref(null);
const trigger = ref(null);
const panelOpen = ref(false);
const unreadCount = ref(0);
const currentPage = ref(1);
const hasNextPage = ref(false);
const loading = ref(false);
const panelStyle = ref({});
const dimRead = ref(false);
const dimTimer = ref(null);
const markingAllRead = ref(false);

const loadCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "[]");
  } catch {
    return [];
  }
};

const notifications = ref(loadCache());

const saveCache = () => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(notifications.value.slice(0, CACHE_LIMIT)));
  } catch {
    // ignore storage errors
  }
};

const notificationChannel = {
  received: notification => {
    if (!notifications.value.find(n => n.id === notification.id)) {
      notifications.value.unshift(notification);
      unreadCount.value++;
      saveCache();
    }
  },
};

const closePanel = () => {
  panelOpen.value = false;
  clearTimeout(dimTimer.value);
  dimRead.value = false;
  document.removeEventListener("click", handleOutsideClick);
};

const handleOutsideClick = e => {
  if (root.value && !root.value.contains(e.target)) {
    closePanel();
  }
};

const updatePanelPosition = () => {
  if (!trigger.value) return;

  const rect = trigger.value.getBoundingClientRect();
  const left = rect.left + rect.width / 2 - PANEL_WIDTH / 2;

  panelStyle.value = {
    top: rect.bottom + 8 + "px",
    left: Math.max(8, left) + "px",
  };
};

const openPanel = () => {
  updatePanelPosition();
  panelOpen.value = true;
  dimRead.value = false;
  dimTimer.value = setTimeout(() => {
    dimRead.value = true;
  }, 5000);
  nextTick(() => {
    document.addEventListener("click", handleOutsideClick);
  });
};

const togglePanel = () => {
  if (panelOpen.value) {
    closePanel();
  } else {
    openPanel();
  }
};

const mergeNotifications = (apiItems, page) => {
  const existingIds = new Set(notifications.value.map(n => n.id));
  const newItems = apiItems.filter(n => !existingIds.has(n.id));

  if (page === 1) {
    const updatedById = Object.fromEntries(apiItems.map(n => [n.id, n]));
    const updated = notifications.value.map(n => (updatedById[n.id] ? { ...n, ...updatedById[n.id] } : n));

    notifications.value = [...newItems, ...updated];
  } else {
    notifications.value.push(...newItems);
  }

  saveCache();
};

const loadNotifications = async page => {
  if (loading.value) return;

  loading.value = true;

  try {
    const { data } = await backend.index("/api/v3/notifications", {
      params: { page, rowsPerPage: ROWS_PER_PAGE },
    });

    unreadCount.value = data.unread_count;
    const pagination = data.pagination;
    currentPage.value = pagination.page;
    hasNextPage.value = pagination.rowsNumber - pagination.rowsPerPage * page > 0;
    mergeNotifications(data.data, page);
  } catch (error) {
    if (error.response) {
      await useReLogin(error.response.status);
    }
  } finally {
    loading.value = false;
  }
};

const onSentinelVisible = isVisible => {
  if (isVisible && !loading.value && hasNextPage.value) {
    loadNotifications(currentPage.value + 1);
  }
};

const markAllRead = async () => {
  if (markingAllRead.value || unreadCount.value === 0) return;

  markingAllRead.value = true;

  try {
    const { data } = await backend.patch("/api/v3/notifications/read_all", {});

    unreadCount.value = typeof data.unread_count === "number" ? data.unread_count : 0;
    notifications.value = notifications.value.map(n => ({
      ...n,
      read: true,
      read_at: data.read_at || n.read_at,
    }));
    saveCache();
  } catch (error) {
    if (error.response) {
      await useReLogin(error.response.status);
    }
  } finally {
    markingAllRead.value = false;
  }
};

const markNotificationRead = async item => {
  if (item.read) return;

  try {
    const { data } = await backend.patch(`/api/v3/notifications/${item.id}/read`, {});

    if (data.data) {
      const index = notifications.value.findIndex(n => n.id === item.id);

      if (index !== -1) {
        notifications.value.splice(index, 1, { ...notifications.value[index], ...data.data });
      }
    }

    if (typeof data.unread_count === "number") {
      unreadCount.value = data.unread_count;
    }

    saveCache();
  } catch (error) {
    if (error.response) {
      await useReLogin(error.response.status);
    }
  }
};

const handleItemClick = async item => {
  await markNotificationRead(item);

  if (!item.url) return;

  closePanel();
  router.push(item.url).catch(() => {});
};

const formatDate = dateString => {
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true, locale: ru });
  } catch {
    return "";
  }
};

onMounted(() => {
  if (cable) {
    cable._addChannel("NotificationsChannel", notificationChannel, cableContext);
    cable.subscribe({ channel: "NotificationsChannel", token: authToken.value });
  }

  loadNotifications(1);
});

onBeforeUnmount(() => {
  clearTimeout(dimTimer.value);
  document.removeEventListener("click", handleOutsideClick);
  cable?._removeChannel("NotificationsChannel");
});
</script>

<style lang="scss" scoped>
.notification-bell {
  display: inline-flex;
  align-items: center;
  margin-right: 4px;

  .bell-trigger {
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;

    .q-icon {
      color: var(--theme-icon-fill);
      font-size: 20px;
    }

    .bell-badge {
      position: absolute;
      top: -2px;
      right: -2px;
      background: #f44336;
      color: white;
      border-radius: 10px;
      font-size: 9px;
      min-width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 3px;
      font-weight: 700;
      line-height: 1;
    }
  }

  .notification-panel {
    position: fixed;
    width: 380px;
    max-height: 460px;
    background: var(--navbar-sublinks-menu-background);
    border-radius: 8px;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgb(0 0 0 / 12%);
    padding: 10px 5px;

    .notification-panel-arrow {
      position: absolute;
      width: 14px;
      height: 14px;
      transform: rotate(45deg);
      background: var(--navbar-sublinks-menu-background);
      top: -7px;
      left: 50%;
      margin-left: -7px;
    }

    .notification-panel-header {
      padding: 4px 12px 10px;
      font-weight: 600;
      font-size: 14px;
      color: var(--navbar-sublink-text-color);
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      .notification-read-all {
        font-size: 12px;
        font-weight: 400;
        flex-shrink: 0;
      }
    }

    .notification-panel-body {
      overflow-y: auto;
      flex: 1;
      min-height: 0;

      .notification-item {
        padding: 10px 12px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        cursor: default;

        &--unread {
          .notification-item-text {
            font-weight: 600;
          }
        }

        &--dim {
          .notification-item-text,
          .notification-item-date {
            transition: opacity 0.6s ease;
            opacity: 0.45;
          }
        }

        &--clickable {
          cursor: pointer;
        }

        &:hover {
          background: var(--navbar-sublink-background-hover);
          border-radius: 8px;
        }

        .notification-item-text {
          font-size: 13px;
          line-height: 1.4;
        }

        .notification-item-date {
          font-size: 11px;
          color: var(--navbar-sublink-text-color);
          opacity: 0.55;
          margin-top: 4px;
        }
      }

      .notification-sentinel {
        height: 1px;
      }

      .notification-loading {
        display: flex;
        justify-content: center;
        padding: 12px;
      }

      .notification-empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--navbar-sublink-text-color);
        opacity: 0.6;
        font-size: 14px;
      }
    }
  }
}
</style>
